import {useCallback, useEffect, useState} from 'react';

import {getEnabledAccountCrmModules} from 'src/openapi/admin/apis/CrmModuleSettingsControllerApi';
import {getUserCrmSettings, saveUserCrmSettings} from 'src/openapi/admin/apis/UserCrmSettingsControllerApi';
import {CrmType} from 'src/openapi/admin/models/CrmType';
import {useAppDispatch} from 'src/store/hooks';
import {getError} from 'src/utils/get-error';
import {checkIsChanged, discardChanges} from 'src/utils/use-case-utils';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {Error} from 'src/openapi/admin/models/Error';
import type {UserCrmSettings} from 'src/openapi/admin/models/UserCrmSettings';

type Props = {
    userId: number | null,
    accountId: number,
};

const DEFAULT_USER_CRM_SETTINGS: UserCrmSettings = {
    crmModules: [],
    syncFromPlatformDisabled: false,
    disableCreationFromPlatform: false,
};

export const useUserCrmSettings = ({userId, accountId}: Props) => {
    const apiDispatch = useAppDispatch();

    const [isLoading, setIsLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<Error | null>(null);

    const [initialData, setInitialData] = useState<UserCrmSettings>(DEFAULT_USER_CRM_SETTINGS);
    const [data, setData] = useState(DEFAULT_USER_CRM_SETTINGS);

    useEffect(() => {
        const loadUserCrmSettings = async (currentUserId: number) => {
            setIsLoading(true);
            const response = await apiDispatch(getUserCrmSettings({userId: currentUserId}));
            if (response.payload.success) {
                setInitialData(response.payload.data);
                setData(response.payload.data);
                setLoadingError(null);
            } else {
                setLoadingError(response.payload.data);
            }
            setIsLoading(false);
        };

        const loadEnabledCrmModules = async () => {
            setIsLoading(true);
            const response = await apiDispatch(getEnabledAccountCrmModules({accountId}));
            if (response.payload.success) {
                const loadedData = {
                    crmModules: response.payload.data.map(crmType => ({
                        crmType,
                        crmId: '',
                        enabled: false,
                        ...crmType !== CrmType.DEALCLOUDCRM ? {
                            isAdmin: false,
                            isImportDisabled: false,
                        } : {},
                    })),
                    syncFromPlatformDisabled: false,
                    disableCreationFromPlatform: false,
                };
                setInitialData(loadedData);
                setData(loadedData);
                setLoadingError(null);
            } else {
                setLoadingError(response.payload.data);
            }
            setIsLoading(false);
        };

        if (userId) {
            loadUserCrmSettings(userId);
        } else {
            loadEnabledCrmModules();
        }
    }, [userId, accountId, apiDispatch]);

    const saveChanges = useCallback(async (proceedUserId: number, options?: SaveOptions) => {
        let error: string | null = null;

        const saveUserCrmSettingsResponse = await apiDispatch(saveUserCrmSettings({
            userId: proceedUserId,
            userCrmSettingsSaveRequest: {...data, reason: options?.reason},
        }));

        if (saveUserCrmSettingsResponse.payload.error) {
            error = getError({error: saveUserCrmSettingsResponse.payload.data, separator: ' '});
        } else {
            setInitialData(data);
        }

        return error;
    }, [data, apiDispatch]);

    return {
        isLoading,
        loadingError,
        isChanged: checkIsChanged(initialData, data),
        data,
        setData,
        saveChanges,
        discardChanges: () => discardChanges(initialData, setData),
    };
};
